import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Collapse,
  Drawer,
  Group,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton
} from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconFigmaFilter } from '@/assets/icons';
import { formatDateRange } from '@/utils/format';

import { DateRangeField } from './DateRangeField';
import { SelectField } from './SelectField';

export interface FilterOption {
  value: string;
  label: string;
}

interface FilterOptionButtonProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

export const FilterOptionButton = ({ label, selected, onClick }: FilterOptionButtonProps) => (
  <UnstyledButton
    onClick={onClick}
    aria-pressed={selected}
    h={40}
    px={12}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      borderRadius: 999,
      fontSize: 14,
      fontWeight: selected ? 600 : 400,
      border: `1px solid ${selected ? 'var(--color-accent-primary)' : 'var(--color-border-default)'}`,
      backgroundColor: selected ? 'var(--color-accent-primary)' : 'var(--color-bg-surface)',
      color: selected ? 'var(--color-text-on-accent)' : 'var(--color-text-primary)'
    }}
  >
    {label}
  </UnstyledButton>
);

export const FilterSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack gap={10}>
    <Text
      fz={11}
      fw={600}
      tt="uppercase"
      style={{ letterSpacing: 0.4, color: 'var(--color-text-muted)' }}
    >
      {title}
    </Text>
    {children}
  </Stack>
);

/** Kvadratni gumb s ikonom filtera; točkica u kutu kad je bilo koji filter aktivan. */
export const FilterIconButton = ({
  isActive,
  onClick,
  label
}: {
  isActive: boolean;
  onClick: () => void;
  label: string;
}) => (
  <UnstyledButton
    onClick={onClick}
    aria-label={label}
    w={44}
    h={44}
    style={{
      position: 'relative',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-surface)',
      color: 'var(--color-text-primary)'
    }}
  >
    <IconFigmaFilter size={18} />
    {isActive && (
      <Box
        w={9}
        h={9}
        style={{
          position: 'absolute',
          top: 7,
          right: 7,
          borderRadius: 999,
          backgroundColor: 'var(--color-accent-primary)',
          border: '1.5px solid var(--color-bg-surface)'
        }}
      />
    )}
  </UnstyledButton>
);

export const filterSheetStyles = {
  content: {
    backgroundColor: 'var(--color-bg-surface)',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '85dvh',
    flex: '0 0 auto',
    height: 'auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden'
  },
  header: { backgroundColor: 'var(--color-bg-surface)', flexShrink: 0 },
  title: { flex: 1, marginRight: 12 },
  body: {
    padding: '4px 16px calc(16px + env(safe-area-inset-bottom))',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto' as const,
    overscrollBehavior: 'contain' as const
  }
};

export interface MobileListFilter {
  key: string;
  /** Naslov sekcije u sheetu i oznaka u sažetku ("Partner: …"). */
  label: string;
  options: FilterOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** Vrijednost koja znači "bez filtra" — null za izborne selecte, npr. 'ALL' za status. */
  emptyValue?: string | null;
  /**
   * 'buttons' = mreža gumba kao na nadzornoj ploči, za mali fiksni skup kratkih opcija (status).
   * 'select' (default) = pretraživi select, za liste entiteta koje rastu i imaju duge nazive
   * (partneri, objekti) — u mreži gumba bi se prelijevale i ne bi stale pregledno.
   */
  variant?: 'buttons' | 'select';
  /**
   * Raspon datuma koji se otvara ispod opcija kad je odabrana opcija `showWhen` (npr. razdoblje
   * "Prilagođeno"). Dio je istog nacrta — primjenjuje se tek na "Primijeni".
   */
  dateRange?: {
    showWhen: string;
    label: string;
    value: DateRangeValue;
    onChange: (value: DateRangeValue) => void;
  };
}

type DateRangeValue = [string | null, string | null];
const EMPTY_RANGE: DateRangeValue = [null, null];

const emptyOf = (filter: MobileListFilter) => filter.emptyValue ?? null;

/**
 * Mobilni filteri liste, isti obrazac kao nadzorna ploča: pretraga + ikona filtera u istom redu,
 * ispod sitnim slovima sažetak svih odabranih parametara, a ikona otvara donji sheet s ostalim
 * filterima. Izmjene u sheetu su nacrt — primjenjuju se tek na "Primijeni".
 */
export const MobileListFilters = ({
  search,
  filters
}: {
  /** Bez pretrage (npr. Board) sažetak stoji lijevo u redu s ikonom, kao na nadzornoj ploči. */
  search?: ReactNode;
  filters: MobileListFilter[];
}) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [rangeDraft, setRangeDraft] = useState<Record<string, DateRangeValue>>({});

  const isActive = filters.some((filter) => filter.value !== emptyOf(filter));
  const isDraftActive = filters.some((filter) => draft[filter.key] !== emptyOf(filter));

  const labelOf = (filter: MobileListFilter, value: string | null) =>
    filter.options.find((option) => option.value === value)?.label ?? t('common.filters.all');

  const summaryValueOf = (filter: MobileListFilter) => {
    const range = filter.dateRange;
    if (range && filter.value === range.showWhen && (range.value[0] || range.value[1])) {
      return formatDateRange(range.value[0] ?? undefined, range.value[1] ?? undefined);
    }
    return labelOf(filter, filter.value);
  };

  const summary = filters.map((filter) => `${filter.label}: ${summaryValueOf(filter)}`).join(' · ');

  const open = () => {
    setDraft(Object.fromEntries(filters.map((filter) => [filter.key, filter.value])));
    setRangeDraft(
      Object.fromEntries(
        filters.filter((f) => f.dateRange).map((f) => [f.key, f.dateRange?.value ?? EMPTY_RANGE])
      )
    );
    setOpened(true);
  };

  const apply = () => {
    for (const filter of filters) {
      if (draft[filter.key] !== filter.value) filter.onChange(draft[filter.key]);
      const range = filter.dateRange;
      if (range) {
        const next = draft[filter.key] === range.showWhen ? rangeDraft[filter.key] : EMPTY_RANGE;
        if (next[0] !== range.value[0] || next[1] !== range.value[1]) range.onChange(next);
      }
    }
    setOpened(false);
  };

  const setDraftValue = (key: string, value: string | null) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const filterButton = filters.length > 0 && (
    <FilterIconButton isActive={isActive} onClick={open} label={t('common.filters.title')} />
  );
  const summaryText = filters.length > 0 && (
    <Text fz={12} style={{ color: 'var(--color-text-secondary)' }}>
      {summary}
    </Text>
  );

  return (
    <Stack gap={6}>
      {search ? (
        <>
          <Group gap={8} wrap="nowrap" align="center">
            <Box style={{ flex: 1, minWidth: 0 }}>{search}</Box>
            {filterButton}
          </Group>
          {summaryText}
        </>
      ) : (
        <Group gap={12} wrap="nowrap" align="center" justify="space-between">
          <Box style={{ flex: 1, minWidth: 0 }}>{summaryText}</Box>
          {filterButton}
        </Group>
      )}

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="bottom"
        title={
          <Group justify="space-between" wrap="nowrap" w="100%">
            <Text fz={15} fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {t('common.filters.title')}
            </Text>
            {isDraftActive && (
              <UnstyledButton
                onClick={() => {
                  setDraft(
                    Object.fromEntries(filters.map((filter) => [filter.key, emptyOf(filter)]))
                  );
                  setRangeDraft({});
                }}
                fz={14}
                fw={600}
                style={{ color: 'var(--color-text-link)' }}
              >
                {t('common.filters.reset')}
              </UnstyledButton>
            )}
          </Group>
        }
        styles={filterSheetStyles}
      >
        <Stack gap={20}>
          {filters.map((filter) => {
            const draftValue = draft[filter.key] ?? null;
            if (filter.variant !== 'buttons') {
              return (
                <FilterSection key={filter.key} title={filter.label}>
                  <SelectField
                    hideLabel
                    aria-label={filter.label}
                    placeholder={t('common.filters.all')}
                    data={filter.options}
                    value={draftValue}
                    onChange={(value) => setDraftValue(filter.key, value ?? emptyOf(filter))}
                    searchable
                    clearable
                  />
                </FilterSection>
              );
            }
            // Izborni filter (emptyValue null) nema "Svi" među svojim opcijama — dodaje se ovdje.
            const buttonOptions =
              emptyOf(filter) === null
                ? [{ value: '', label: t('common.filters.all') }, ...filter.options]
                : filter.options;
            const range = filter.dateRange;
            return (
              <FilterSection key={filter.key} title={filter.label}>
                <div>
                  <SimpleGrid cols={2} spacing={8}>
                    {buttonOptions.map((option) => (
                      <FilterOptionButton
                        key={option.value}
                        label={option.label}
                        selected={(draftValue ?? '') === option.value}
                        onClick={() =>
                          setDraftValue(filter.key, option.value === '' ? null : option.value)
                        }
                      />
                    ))}
                  </SimpleGrid>
                  {range && (
                    <Collapse in={draftValue === range.showWhen} transitionDuration={200}>
                      <Box pt={18}>
                        <DateRangeField
                          label={range.label}
                          valueFormat="DD.MM.YYYY."
                          value={rangeDraft[filter.key] ?? EMPTY_RANGE}
                          onChange={(value) =>
                            setRangeDraft((current) => ({ ...current, [filter.key]: value }))
                          }
                          clearable
                        />
                      </Box>
                    </Collapse>
                  )}
                </div>
              </FilterSection>
            );
          })}

          <Button fullWidth onClick={apply}>
            {t('common.filters.apply')}
          </Button>
        </Stack>
      </Drawer>
    </Stack>
  );
};
